import { Link } from "react-router-dom";
import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import TrainingCard from "@/components/site/TrainingCard";
import CTASection from "@/components/site/CTASection";
import FAQSection from "@/components/site/FAQSection";
import { TRAINING_CATEGORIES, FAQS_TRAINING } from "@/data/content";

const EXAMPLE_TRAINING_FORMATS = [
  {
    title: "VR Traction course",
    body: "Immersive traction training designed to build cab familiarity, systems knowledge and operational confidence.",
    to: "/vr-traction",
  },
  {
    title: "VR PTS course",
    body: "Safe VR scenarios to teach trackside awareness, hazard recognition and Personal Track Safety before live access.",
    to: "/vr-pts",
  },
  {
    title: "eLearning course",
    body: "Structured online learning with knowledge checks, assessments and completion evidence for railway teams.",
    to: "/elearning",
  },
];

export default function Training() {
  return (
    <>
      <SEO
        title="Training & Courses | UKTDA"
        description="Specialist railway training and professional development — from train driver competence and instructor capability to NTS, human factors, fatigue, and safety-critical communication."
        path="/training"
      />

      <PageHero
        eyebrow="Training & courses"
        title="Specialist programmes across the railway competence spectrum."
        intro="Our training is grounded in safety-critical practice and designed for individuals, teams and organisations across the modern railway."
      />

      <section className="py-20 md:py-28 bg-white" data-testid="training-grid">
        <div className="container-rail">
          <div className="max-w-3xl mb-12">
            <p className="label-eyebrow text-safety">Training areas</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Practical development for drivers, instructors, managers and safety-critical teams.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              Each training area can be delivered as a focused workshop, a structured course, a CPD
              session, or part of a wider competence development programme. Content can be adapted
              for individual learners, operational teams, training providers, heritage railways and
              rail organisations.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {TRAINING_CATEGORIES.map((training) => (
              <TrainingCard key={training.code} {...training} />
            ))}
          </div>

          <div
            className="mt-12 rounded-md border border-slate-200 bg-slate-50 p-6 md:p-8"
            data-testid="training-delivery-note"
          >
            <p className="label-eyebrow text-safety">Delivery</p>

            <h2 className="mt-3 font-display font-bold text-2xl text-navy">
              Flexible delivery built around the learner and the operational context.
            </h2>

            <p className="mt-3 text-base text-slate-700 leading-relaxed max-w-3xl">
              Courses and development programmes can be delivered for individuals, teams or
              organisations depending on requirements. Programmes are designed around your
              operational context, competence framework, risks, learner population and desired
              outcomes.
            </p>
          </div>

          <section className="mt-16 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="training-example-formats">
            <div className="max-w-3xl">
              <p className="label-eyebrow text-safety">Training examples</p>
              <h2 className="mt-4 font-display font-bold text-3xl text-navy leading-tight">
                Example formats for immersive and online railway training.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Explore sample training routes that demonstrate how VR and eLearning can work within modern railway competence programmes.
              </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {EXAMPLE_TRAINING_FORMATS.map((example) => (
                <Link
                  key={example.title}
                  to={example.to}
                  className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 transition hover:-translate-y-0.5 hover:border-safety/60"
                >
                  <h3 className="font-display font-bold text-xl text-navy">
                    {example.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {example.body}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-safety">
                    View example
                  </span>
                </Link>
              ))}
            </div>
          </section>

        </div>
      </section>

      <FAQSection
        items={FAQS_TRAINING}
        title="Training & courses FAQs"
        intro="The questions most often asked about how our training programmes are designed, delivered and positioned."
        schemaId="faq-training"
      />

      <CTASection
        eyebrow="Plan training"
        title="Discuss a training programme for your team or career."
        intro="Tell us what you are trying to achieve and we’ll help identify the most appropriate training, course or development route."
        primary={{
          label: "Contact us",
          to: "/contact",
          testId: "training-cta-contact",
        }}
        secondary={{
          label: "For organisations",
          to: "/for-organisations",
          testId: "training-cta-org",
        }}
      />
    </>
  );
}